import React from "react";

import { LiveBlogItem } from "~components/shared/liveblog/LiveBlogItem";
import {
    type MediaConfig,
    DEFAULT_MEDIA_PRIORITY,
    Media,
    MediaFormat,
} from "~components/shared/media/";
import PhoenixRelatedItems from "~components/shared/phoenix-related-items/PhoenixRelatedItems";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import TickerPills from "~components/shared/ticker-pills/TickerPills";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import {
    type CuratedItem,
    type VariationComponent,
    getRelatedStories,
    getIsOpinionPageTheme,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentLede.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentLede.module.css";

export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.carousel,
    ...DEFAULT_MEDIA_PRIORITY,
];
const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.media,
    desktopMaxWidth: 459,
    mobileMaxWidth: 715,
};

const MAX_STORY_COUNT = 4;

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];

const PrimaryStory = (props: { story: CuratedItem; theme?: string }) => {
    const { story, theme } = props;

    return (
        <div className={styles.primaryStory} data-testid="primary-story">
            <Media
                source={story}
                priority={MEDIA_PRIORITY}
                config={MEDIA_CONFIG}
            />
            <div className={styles.storyBlock}>
                <StoryBlock
                    storyIndex={1}
                    story={story}
                    withDek
                    isBylineEligible
                    withOptionalEyebrow={!getIsOpinionPageTheme(theme)}
                />
                <TickerPills storyIndex={1} tickerIds={story.terminalTickers} />
                <PhoenixRelatedItems
                    storyIndex={1}
                    relatedItems={getRelatedStories(story) || []}
                    relatedTitle={story.relatedTitle}
                />
            </div>
        </div>
    );
};

const SecondaryStories = (props: { stories: CuratedItem[] }) => {
    const { stories } = props;

    return (
        <div
            className={styles.secondaryStories}
            data-testid="secondary-stories"
        >
            {stories?.map((story, index) => (
                <StoryBlock
                    storyIndex={2 + index}
                    key={story.id}
                    className={styles.secondaryStory}
                    isExtraSmallHeadline
                    isSecondaryStory
                    story={story}
                />
            ))}
        </div>
    );
};

export default function LineupContentLede(props: VariationComponent) {
    const { items: initialItems = [], theme } = props;

    const items = initialItems.slice(0, MAX_STORY_COUNT);
    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    const [primaryStory, ...secondaryStories] = items;
    const hasSecondaryStories = items.length > 2;
    const isLiveBlog = primaryStory.type === "liveblog";

    return (
        <VariantBase
            source={props}
            className={styles.LineupContentLede}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            {isLiveBlog ? (
                <LiveBlogItem
                    className={styles.liveblog}
                    item={primaryStory}
                    enableMediaCarousel
                />
            ) : (
                <PrimaryStory story={primaryStory} theme={theme} />
            )}
            {hasSecondaryStories && (
                <SecondaryStories stories={secondaryStories} />
            )}
        </VariantBase>
    );
}
